import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef } from "react";
import { AppState } from "react-native";

import { log } from "@/logger";
import { query } from "@/query/queries";

export type DeferredAttachmentStaffNotify = {
    rememberAdded: (id: number) => void;
    forgetRemoved: (id: number) => void;
    /** Drop pending ids without emailing (e.g. after a successful resubmit). */
    clear: () => void;
    /** Send one staff email for pending ids, then clear. */
    flush: () => void;
    onAttachmentsBusyChange: (busy: boolean) => void;
    onPickerSessionChange: (active: boolean) => void;
};

type Options = {
    /**
     * When true (default), flush on screen blur / app background.
     * Resubmit-from-list uses false and flushes only when the dialog closes.
     */
    autoFlush?: boolean;
};

/**
 * Collects attachment ids uploaded with skip_staff_email.
 * With autoFlush, sends one staff email on blur or background unless a picker
 * or upload is still in progress.
 */
export function useDeferredAttachmentStaffNotify(
    options?: Options,
): DeferredAttachmentStaffNotify {
    const autoFlush = options?.autoFlush !== false;
    const deferredIds = useRef<number[]>([]);
    const attachmentsBusy = useRef(false);
    const pickerActive = useRef(false);
    const flushAfterBusy = useRef(false);

    const clear = useCallback(() => {
        deferredIds.current = [];
        flushAfterBusy.current = false;
    }, []);

    const flush = useCallback(() => {
        const ids = deferredIds.current.splice(0);
        flushAfterBusy.current = false;

        if (ids.length === 0) {
            return;
        }

        void query.notifyAddedLeaveRequestAttachment(ids).catch((error) => {
            log.error("notifyAddedLeaveRequestAttachment failed", error);
        });
    }, []);

    const requestFlush = useCallback(
        (flushOptions?: { ignoreBusy?: boolean }) => {
            if (pickerActive.current) {
                return;
            }

            if (attachmentsBusy.current && !flushOptions?.ignoreBusy) {
                flushAfterBusy.current = true;
                return;
            }

            flush();
        },
        [flush],
    );

    const rememberAdded = useCallback((id: number) => {
        if (!deferredIds.current.includes(id)) {
            deferredIds.current.push(id);
        }
    }, []);

    const forgetRemoved = useCallback((id: number) => {
        deferredIds.current = deferredIds.current.filter((item) => item !== id);
    }, []);

    const onAttachmentsBusyChange = useCallback(
        (busy: boolean) => {
            attachmentsBusy.current = busy;

            if (!busy && flushAfterBusy.current) {
                if (pickerActive.current || AppState.currentState === "active") {
                    flushAfterBusy.current = false;
                    return;
                }

                flush();
            }
        },
        [flush],
    );

    const onPickerSessionChange = useCallback((active: boolean) => {
        pickerActive.current = active;
    }, []);

    useFocusEffect(
        useCallback(() => {
            if (!autoFlush) {
                return undefined;
            }

            return () => {
                // Leaving the screen: send what is already uploaded.
                requestFlush({ ignoreBusy: true });
            };
        }, [autoFlush, requestFlush]),
    );

    useEffect(() => {
        if (!autoFlush) {
            return undefined;
        }

        const subscription = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                return;
            }

            // Home button / app switch: wait if an upload is still running.
            requestFlush();
        });

        return () => {
            subscription.remove();
        };
    }, [autoFlush, requestFlush]);

    return {
        rememberAdded,
        forgetRemoved,
        clear,
        flush,
        onAttachmentsBusyChange,
        onPickerSessionChange,
    };
}
